import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from '../../../../environments/environment';
import { receiveMessage, connectionStatusChanged, reconnectionFailed } from '../store/actions/websockets.actions';
import { AuthenticationService } from './authentification.service';
import { SharedAppState } from '../store/app.states';
import { Websocket_message } from '../model/websocket_message.model';

/** Essais de reconnexion avant d'abandonner et de prévenir l'utilisateur. */
const MAX_RECONNECT_ATTEMPTS = 3;

/** Délai avant chaque essai, un par tentative (backoff). */
const RECONNECT_DELAYS_MS = [1000, 3000, 8000];

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    private webSocket: WebSocket = null;
    private readonly apiUrl = environment.ws_server;

    private personaId: string = null;
    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> = null;
    private closedByClient = false;

    constructor(
        private readonly store: Store<SharedAppState>,
        private readonly authenticationService: AuthenticationService
    ) { }

    public connect(personaId: string): void {
        this.personaId = personaId;
        this.closedByClient = false;
        this.reconnectAttempts = 0;
        this.openSocket();
    }

    public send(data: any): void {
        // Envoyer sur une socket qui n'est pas encore ouverte (ou déjà fermée)
        // lève une exception : on ignore, la reconnexion prendra le relais.
        if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
            return;
        }
        this.webSocket.send(JSON.stringify(data));
    }

    public disconnect(): void {
        this.closedByClient = true;
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        if (this.webSocket) {
            this.webSocket.close();
            this.store.dispatch(connectionStatusChanged({ connected: false }));
        }
    }

    private openSocket(): void {
        // CLOSING est un état transitoire : ne tester que CLOSED rendait connect()
        // sans effet pendant la fermeture, donc toute reconnexion immédiate.
        if (this.webSocket
            && (this.webSocket.readyState === WebSocket.OPEN
                || this.webSocket.readyState === WebSocket.CONNECTING)) {
            return;
        }

        const wsAuth = this.authenticationService.getWebSocketAuth(this.personaId);
        this.webSocket = new WebSocket(`${this.apiUrl}${wsAuth}`);

        this.webSocket.addEventListener('open', () => {
            this.reconnectAttempts = 0;
            this.store.dispatch(connectionStatusChanged({ connected: true }));
        });
        this.webSocket.addEventListener('close', () => {
            this.store.dispatch(connectionStatusChanged({ connected: false }));
            this.scheduleReconnect();
        });
        this.webSocket.addEventListener('message', (event: MessageEvent) => {
            const message = JSON.parse(event.data);
            this.store.dispatch(receiveMessage({ message }));
        });
    }

    private scheduleReconnect(): void {
        if (this.closedByClient || this.reconnectTimer || !this.personaId) {
            return;
        }
        if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            this.store.dispatch(reconnectionFailed());
            return;
        }

        const delay = RECONNECT_DELAYS_MS[this.reconnectAttempts];
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.openSocket();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    public prepare_message_data(persona: any, params: any): Websocket_message {
        let data = new Websocket_message();
        data = {
            info: {
                message_type: params.message_type,
                object_id: params.object_id,
                sender: {
                    role: localStorage.getItem('role'),
                    persona_id: persona.id
                }
            },
            action: {
                action_type: params.action_type
            },
            data: params.data
        };
        return data;
    }
}
